import Pluralize from 'pluralize';
import store from '@app/store';

export default class BPCApiRouteHelper {
  enabledLangs = ['en', 'be', 'fr', 'nl', 'src', 'raw', 'es'];

  getEntityPath(entity: string, lang: string = null): string {
    return `${this.getTenantId()}/${this.getLang(lang)}/${this.getPlural(entity)}`;
  }

  getApiPath(path: string, lang: string = null): string {
    return `${this.getTenantId()}/${this.getLang(lang)}/${path}`;
  }

  getTranslatableFieldsPath(entity: string, entityId: number): string {
    return `${this.getEntityPath(entity)}/${entityId}/translatable-fields`;
  }

  getRecaptchaV3Path(lang: string = null): string {
    return `${this.getTenantId()}/${this.getLang(lang)}/recaptcha`;
  }

  getSearchBookingPath(entity: string, lang: string = null): string {
    return `${this.getTenantId()}/raw/${this.getPlural(entity)}`;
  }

  getTenantPath(lang: string = null): string {
    return `${this.getLang(lang)}/centers`;
  }

  getMyTenantsPath(lang: string = null): string {
    return `${this.getLang(lang)}/centers/my-centers`;
  }

  getMyTenantsAndProfilesPath(lang: string = null): string {
    return `${this.getTenantId()}/auth/profiles-tenants`;
  }

  getAccessControlConfigPath(lang: string = null): string {
    return `${this.getTenantId()}/src/access-control-config`;
  }

  getHardDeleteEntityPath(entity: string): string {
    return this.getEntityPath(entity) + '/db';
  }
  getRecoverEntityPath(entity: string): string {
    return this.getEntityPath(entity) + '/recover';
  }

  getBulkDeleteEntityPath(entity: string): string {
    return this.getEntityPath(entity) + '/bulk-delete';
  }

  getBulkHardDeleteEntityPath(entity: string): string {
    return this.getEntityPath(entity) + '/db/bulk-delete';
  }

  getBulkRecoverEntityPath(entity: string): string {
    return this.getEntityPath(entity) + '/bulk-recover';
  }

  getOrderEntityPath(entity: string, lang: string = null): string {
    return this.getEntityPath(entity) + '/order';
  }

  getEntityConfigPath(tenantId: any, entity: string): string {
    const singular = this.getSingular(entity);
    return `${tenantId}/config/${singular}`;
  }

  getMediaCollectionsConfigPath(entity: string): string {
    const singular = this.getSingular(entity);
    return `config/${singular}/mediaCollections`;
  }

  getMediaFileUploadEntityPath(entity: string, entityId: string | number): string {
    return this.getEntityPath(entity) + `/${entityId}/media`;
  }

  getMediaFileDeleteEntityPath(entity: string, entityId: string | number, mediaId: string): string {
    return this.getEntityPath(entity) + `/${entityId}/media/${mediaId}`;
  }

  getEntityFormConfigPath(entity: string, form = 'default'): string {
    const configEntity = entity.replace(/-/g, '_'); // TODO: Manage this in back (provisional)
    const singular = this.getSingular(configEntity);
    return `config/${singular}/forms/${form}`;
  }
  getEntityFilterConfigPath(entity: string, form = 'default'): string {
    const singular = this.getSingular(entity);
    return `config/${singular}/filters/${form}`;
  }

  getEntityDisplayConfigPath(entity: string, display = 'table'): string {
    const singular = this.getSingular(entity);
    return `config/${singular}/displays/${display}`;
  }

  getEntityRelationsConfigPath(entity: string, relation = ''): string {
    const singular = this.getSingular(entity);
    const basePath = `config/${singular}/relations`;
    return relation ? `${basePath}/${relation}` : basePath;
  }
  getEntityStructureConfigPath(entity: string, key = ''): string {
    const singular = this.getSingular(entity);
    const basePath = `${this.getTenantId()}/config/${singular}/structure`;
    return key ? `${basePath}/${key}` : basePath;
  }
  getEntityTypesConfigPath(context: string = ''): string {
    const basePath = 'config/entity-types';
    return context ? `${basePath}/${context}` : basePath;
  }
  getEntityOptionsConfigPath(entity: string, context: string = ''): string {
    const configEntity = entity.replace(/-/g, '_'); // TODO: Manage this in back (provisional)
    const singular = this.getSingular(configEntity);
    const basePath = `config/${singular}/options`;
    return context ? `${basePath}/${context}` : basePath;
  }
  getLoginPath(): string {
    return `oauth/login`;
  }
  getRefreshTokenPath(): string {
    return `oauth/refreshToken`;
  }
  getUserPath(): string {
    return `user`;
  }
  getTranslationsPath(language: string = null): string {
    const lang = language || localStorage.getItem('lang');
    return `${this.getLang(lang)}/translations`;
  }

  getPlural(str: string): string {
    return Pluralize.isSingular(str) ? Pluralize(str) : str;
  }

  getSingular(str: string): string {
    return Pluralize.isPlural(str) ? Pluralize.singular(str) : str;
  }

  addInitialSlash(str: string): string {
    return str.startsWith('/') ? str : `/${str}`;
  }

  removeFinalSlash(str: string): string {
    return str.replace(/\/$/, '');
  }

  getTenantId() {
    return store.getters['tenant/selectedTenantId'];
  }

  getLangCode(lang: string): string {
    // TODO: implement a logic to transform languages string to system accepted languages: like EN_UK -> en
    if (!this.enabledLangs.includes(lang)) {
      const defaultLang = process.env.VUE_APP_DEFAULT_LANG || 'src';
      return defaultLang;
    }
    return lang;
  }

  getLang(lang: string): string {
    if (lang) {
      return this.getLangCode(lang);
    }

    // TODO: Change this end of the function with this code
    // when tested well the toTranslate property of the fields
    // like status, type etc.

    // const savedLang = localStorage.getItem('lang');
    // if (typeof savedLang === 'undefined' || savedLang === null) {
    //   const defaultLang = process.env.VUE_APP_DEFAULT_LANG || 'src';
    //   return this.getLangCode(defaultLang);
    // }
    // return this.getLangCode(savedLang);

    // NOTE: this is what should be changed to not use toTranslate property
    const toTranslate = process.env.VUE_APP_TO_TRANSLATE_IN_API;
    if (toTranslate) {
      lang = localStorage.getItem('lang');
      if (typeof lang === 'undefined' || lang === null) {
        return process.env.VUE_APP_DEFAULT_LANG || 'en'; // TODO - provisional
      } else {
        return lang;
      }
    } else {
      return 'src';
    }
  }
}
